import cls from "classnames";
import { useEffect, useRef, useState } from "react";
import { Switch } from "@headlessui/react";

import { GiftIcon } from "@qoohoo/common/library/Icons";
import { pageview } from "@qoohoo/common/constants/analytics";
import { getMailToLink } from "@qoohoo/common/utils/url";
import { MultiCurrency } from "@qoohoo/common/utils/currency";
import * as Track from "@qoohoo/common/utils/analytics/track";
import Button from "@qoohoo/common/components/library/Button";

import IndexFooter from "components/IndexFooter";
import MainNavbar from "components/Navbar/MainNavbar";
import Testimonials from "components/pricing/Testimonials";
import PricingPlanCard from "components/pricing/PricingPlanCard";
import FAQ from "components/FAQ";

const colors = {
  background: "#F9FAFA",
  primary: "#384AF1",
  accent: "#F4F6FF",
  border: "#E8E8E8",
  border_dark: "#16191B",
  banner: "#FFE9FA",
  switch: {
    background: "#EEF0FE",
    border: "#E2E4FC",
  },
  text: "#16191B",
  text_banner: "#AF4697",
  text_light: "#525252",
  text_button: "#FFFFFF",
  whatsapp: {
    background: "#D5F5D3",
    primary: "#20B038",
  },
  testimonialCard: {
    background: "#F4F6FF",
    border: "#E2E4FC",
  },
  faq: {
    questionBackground: "#F4F6FF",
    answerBackground: "#FFFFFF",
  },
};

const faqs = [
  {
    q: "What payment methods are available for the users to make the payment?",
    a: "You name it, we have it. Users can complete the payments using any preferred modes like UPI, netbanking, Debit/Card and more.",
  },
  {
    q: "Can I accept international payments?",
    a: "Absolutely. Users can pay via PayPal or you can connect Stripe account to accept international payments",
  },
  {
    q: "Can we take the transaction charges from the users?",
    a: "Yes, creators can decide if the transaction charges are to be taken from their users while making the purchase",
  },
  {
    q: "Can I get custom pricing for my qoohoo profile?",
    a: () => (
      <span>
        Indeed. You can connect with us on{" "}
        <a style={{ color: colors.primary }} href="http://wa.me/917669359471">
          support
        </a>{" "}
        to get custom pricing.
      </span>
    ),
  },
];

const currentScreen = pageview.pricing;

export default function Pricing() {
  useEffect(() => {
    Track.pageView(currentScreen);
  }, []);

  return (
    <div className="font-nunito" style={{ background: colors.background }}>
      <MainNavbar isDark={false} currentScreen={currentScreen} />
      <HeroSection />
      {/* <WhatsappBroadcastSection /> */}
      <TestimonialsSection />
      <FAQSection />
      <IndexFooter footerBackground={colors.background} isLightMode />
    </div>
  );
}

export function HeroSection() {
  const [isBilledMonthly, setIsBilledMonthly] = useState(false);

  const MONTHLY_BILLING_PRICE = 9999;
  const QUARTERLY_BILLING_PRICE = 7999;

  const proPlanPrice = isBilledMonthly
    ? MONTHLY_BILLING_PRICE
    : QUARTERLY_BILLING_PRICE;

  const mailToBody = `Hi qoohoo team

I am interested in knowing the next steps. Please reach out to me on the below contact details.

Whatsapp/Phone number: 

Thanks
Name:`;

  const proPlanMail = getMailToLink(
    "support@qoohoo.in",
    "Query regarding pro plan pricing",
    mailToBody
  );
  const starterPlanMail = getMailToLink(
    "support@qoohoo.in",
    "Query regarding starter plan pricing",
    mailToBody
  );
  const customPlanMail = getMailToLink(
    "support@qoohoo.in",
    "Query regarding custom plan pricing",
    mailToBody
  );

  const EarlyPricingLabel = () => (
    <div
      className="py-0.5 px-2 text-[0.8125rem] leading-[1.125rem] font-bold rounded select-none"
      style={{ background: colors.banner, color: colors.text_banner }}
    >
      Early Pricing
    </div>
  );

  const StarterPlanData = [
    {
      title: "Payments",
      items: [
        {
          label: () => (
            <>
              <span
                className="text-sm md:text-base leading-normal select-none"
                style={{ color: colors.text }}
              >
                7%&nbsp;
                <span className="text-xs md:text-sm relative strike-through after:-rotate-[18deg]">
                  <span className="opacity-60">10%</span>
                </span>
                &nbsp;transaction fee
              </span>
              <EarlyPricingLabel />
            </>
          ),
          hideIcon: true,
        },
        {
          label: "Collect international payments",
          hideIcon: true,
        },
      ],
    },
    {
      title: "Features",
      items: [
        {
          label: "Multiple monetisation tools",
          tooltip: "Monetise from courses, locked content, slot booking, WhatsApp & Telegram payments",
        },
        {
          label: "Custom subscription plans",
          tooltip: "Create multiple customised subscription plans for your audience",
        },
        {
          label: "Instant payout",
        },
        {
          label: "Payment pages",
          tooltip: "Get custom payment page for taking payments of your offerings",
        },
        {
          label: "Email support",
        },
      ],
    },
    {
      title: "Growth tools",
      items: [
        {
          label: "Discount coupons",
        },
        {
          label: "Basic analytics",
        },
      ],
    },
  ];

  const ProPlanData = [
    {
      title: "Payments",
      items: [
        {
          label: () => (
            <>
              <span
                className="text-sm md:text-base leading-normal"
                style={{ color: colors.text }}
              >
                5%&nbsp;
                <span className="text-xs md:text-sm relative strike-through after:-rotate-[25deg]">
                  <span className="opacity-60">7%</span>
                </span>
                &nbsp;transaction fee
              </span>
              <EarlyPricingLabel />
            </>
          ),
          hideIcon: true,
        },
        {
          label: "Collect international payments",
          hideIcon: true,
        },
      ],
    },
    {
      title: "All starter features, plus",
      items: [
        {
          label: "Encrypted courses & watermark",
        },
        {
          label: "Collect custom user details",
          tooltip: "Collect custom user details during payment",
        },
        {
          label: "Instant & Partial payout",
          tooltip: "Withdraw your partial or full payouts instantly",
        },
        {
          label: "Remove qoohoo branding",
        },
        {
          label: "High priority support",
        },
      ],
    },
    {
      title: "Growth tools",
      items: [
        {
          label: "Marketing cards",
          tooltip: "Share a automatic designed poster with your audience",
        },
        {
          label: "Growth analytics",
          tooltip: "Get detailed insights of your users for your growth & marketing",
        },
        {
          label: "Third party integrations",
          tooltip: "Integrate 10+ tools including google analytics, whatsapp & zoom",
        },
        {
          label: "User retargeting",
          tooltip: "Retarget your users through WhatsApp marketing",
        },
      ],
    },
  ];

  const Title = (
    <div className="flex flex-col gap-2 md:gap-4 items-center mx-auto">
      <div
        className="flex items-center gap-1 px-2 py-0.5 md:py-1 rounded"
        style={{ background: colors.banner, color: colors.text_banner }}
      >
        <span className="text-xs md:text-base font-bold leading-normal flex items-center gap-1 md:gap-2">
          <GiftIcon
            className="h-3 w-3 md:h-4 md:w-4"
            color={colors.text_banner}
          />
          Get early bird offer
        </span>
      </div>
      <h1
        className="font-bold text-2xl md:text-5xl leading-normal text-center"
        style={{ color: colors.text }}
      >
        <span style={{ color: colors.primary }}>Simple plans</span> for every
        stage
      </h1>
      <h2
        className="text-base leading-normal md:text-2xl text-center"
        style={{ color: colors.text }}
      >
        Choose a plan that fits your audience & business goals
      </h2>
    </div>
  );

  const BillingSwitch = (
    <div className="mt-6 md:mt-[3.75rem]">
      <Switch checked={isBilledMonthly} onChange={setIsBilledMonthly}>
        <span
          className="bg-white h-8 md:h-9 flex items-center rounded-lg relative overflow-hidden font-semibold"
          style={{ border: `1px solid ${colors.switch.border}` }}
        >
          <span
            className={cls(
              "inline-block h-[calc(100%+2px)] transition-all duration-300 rounded-lg ease-in-out transform absolute z-0 border",
              isBilledMonthly
                ? "left-0 w-16 md:w-20 border-l-0"
                : "left-16 md:left-20 w-36 md:w-40 border-r-0"
            )}
            style={{ background: colors.switch.background, borderColor: colors.switch.border}}
          />
          <span
            className="w-16 md:w-20 text-xs md:text-sm transition-colors relative z-1"
            style={{ color: !isBilledMonthly ? colors.text : colors.primary }}
          >
            Monthly
          </span>
          <span
            className="w-36 md:w-40 text-xs md:text-sm transition-colors relative z-1"
            style={{ color: isBilledMonthly ? colors.text : colors.primary }}
          >
            Quarterly (save 20%)
          </span>
        </span>
      </Switch>
    </div>
  );

  const BottomCard = (
    <div
      className="mt-4 rounded-lg w-full max-w-md md:max-w-4xl bg-white flex flex-col md:flex-row justify-between md:items-center gap-4 md:gap-0"
      style={{ border: `1px solid ${colors.border}` }}
    >
      <div className="flex flex-col order-1 md:order-0 p-4 md:p-6 md:pr-0">
        <h1
          className="text-xl md:text-2xl leading-normal font-bold"
          style={{ color: colors.text }}
        >
          Is your monthly earnings more than $10K?
        </h1>
        <p
          className="mt-1 md:mt-3 text-sm md:text-base leading-normal"
          style={{ color: colors.text, opacity: 0.6 }}
        >
          Maximize your earnings as a creator! Contact us today to discuss
          custom pricing options tailored to meet the unique needs of your
          business.
        </p>
        <Button
          className="w-[7.5rem] md:w-52 !h-9 md:!h-10 !rounded md:!rounded-md mt-4 md:mt-6"
          style={{ color: colors.text_button, background: colors.primary }}
          onClick={() => {
            window.open(customPlanMail, "_blank");
          }}
        >
          Contact us
        </Button>
      </div>
      <div className="w-full md:w-auto md:h-full md:self-stretch shrink-0 order-0 md:order-1">
        <img
          src="/assets/images/social-earnings-desktop.svg"
          className="h-full w-full"
        />
      </div>
    </div>
  );

  return (
    <main className="py-9 px-5 md:py-[3.75rem] flex flex-col items-center relative overflow-hidden">
      {Title}
      {BillingSwitch}
      <div className="mt-4 md:mt-9 flex flex-col md:flex-row md:items-start gap-4 w-full max-w-md md:max-w-4xl">
        <PricingPlanCard
          title="STARTER PLAN"
          price="FREE"
          buttonText="Start Now"
          buttonStyle={{
            border: `2px solid ${colors.primary}`,
            color: colors.primary,
          }}
          onButtonClick={() => {
            window.open(starterPlanMail, "_blank");
          }}
          className="md:mt-[2.625rem]"
          colors={colors}
          data={StarterPlanData}
          style={{
            background: "#FFFFFF",
            border: `1px solid ${colors.border}`,
          }}
        />
        <PricingPlanCard
          title="PRO PLAN"
          titleColor={colors.primary}
          cardBanner={{
            icon: "/assets/icons/star-white.svg",
            label: "MOST  POPULAR",
          }}
          price={() => (
            <span>
              <span style={{ color: colors.text }}>
                <span className="text-2xl md:text-[2rem] leading-10 font-extrabold">
                  {MultiCurrency.formatAmountWithSymbol(proPlanPrice, "INR")}
                </span>
                <span className="text-sm md:text-base font-semibold">/mo</span>
              </span>
              {!isBilledMonthly && (
                <span
                  className="ml-1 strike-through relative"
                  style={{ color: colors.text, opacity: 0.4 }}
                >
                  <span className="text-base md:text-xl font-bold">
                    {MultiCurrency.formatAmountWithSymbol(
                      MONTHLY_BILLING_PRICE,
                      "INR"
                    )}
                  </span>
                  <span className="text-sm md:text-base">/mo</span>
                </span>
              )}
            </span>
          )}
          priceDescription={
            isBilledMonthly ? "Billed monthly" : "Billed quarterly"
          }
          buttonText="Contact us"
          buttonStyle={{
            background: colors.primary,
            color: colors.text_button,
          }}
          onButtonClick={() => {
            window.open(proPlanMail, "_blank");
          }}
          colors={colors}
          data={ProPlanData}
          style={{
            background: "#FFFFFF",
            border: `2px solid ${colors.primary}`,
          }}
        />
      </div>
      {BottomCard}
    </main>
  );
}

export function WhatsappBroadcastSection() {
  const inputRef = useRef(null);
  const [sliderValue, setSliderValue] = useState(30_000);
  const [percentage, setPercentage] = useState(22.22);
  const ratePerMessage = 0.06;

  const onSliderValueChange = (e) => {
    const value = e.target.value;
    const percentage =
      ((inputRef.current.value - inputRef.current.min) /
        (inputRef.current.max - inputRef.current.min)) *
      100;
    setPercentage(percentage);
    setSliderValue(value);
  };

  return (
    <section
      className="py-9 px-5 md:py-[3.75rem] flex flex-col items-center relative overflow-hidden gap-6 md:gap-9"
      style={{ background: colors.whatsapp.background }}
    >
      <img
        src="/assets/icons/whatsapp.svg"
        className="absolute -left-5 -bottom-5 md:-left-8 md:-bottom-16 h-[120px] w-[120px]  md:h-[250px] md:w-[250px] opacity-20"
      />
      <div className="text-center mx-auto">
        <h1
          className="font-bold text-2xl md:text-4xl leading-normal"
          style={{ color: colors.text }}
        >
          WhatsApp broadcasting
        </h1>
        <h2
          className="text-sm md:text-xl leading-normal mt-1 md:mt-4"
          style={{ color: colors.text }}
        >
          Send personalised messages to gain & retain users by making them
          engage with your content.
        </h2>
      </div>
      <div className="w-full flex flex-col md:flex-row items-center justify-center gap-5 md:gap-9">
        <div className="w-full max-w-4xl flex-col">
          <div className="relative w-full">
            <input
              type="range"
              min={10_000}
              max={100_000}
              step={1000}
              className="green-slider w-full"
              value={sliderValue}
              onChange={onSliderValueChange}
              ref={inputRef}
              style={{
                background: `linear-gradient(to right, ${colors.whatsapp.primary} 0%, ${colors.whatsapp.primary} ${percentage}%, #00000026 ${percentage}%, #00000026 100%)`,
              }}
            />
            <div
              className="absolute h-8 w-8 md:h-7 md:w-11 rounded-sm flex items-center justify-center font-bold text-xs md:text-base mt-[6px] md:mt-[10px]"
              style={{
                background: colors.whatsapp.primary,
                left: `${percentage}%`,
                transform: "translateX(calc(-50% + 6px))",
              }}
            >
              {Math.ceil(sliderValue / 1000)}k
            </div>
          </div>
          <div
            className="flex justify-between w-full mt-2"
            style={{ color: colors.text_light }}
          >
            <span className="text-xs leading-4 md:text-base md:leading-5">
              10k
            </span>
            <span className="text-xs leading-4 md:text-base md:leading-5">
              100k+
            </span>
          </div>
        </div>
        <div
          className="flex flex-col items-center shrink-0"
          style={{ color: colors.text }}
        >
          <div className="flex items-center gap-1 relative">
            <span className="font-semibold text-sm">Your total cost</span>
            {/* <Tooltip
              icon="/assets/icons/icon-question-mark.svg"
              iconClassName="h-[0.875rem] w-[0.875rem] md:h-5 md:w-5"
              description="TODO"
            /> */}
          </div>
          <span className="font-bold text-2xl mt-1">
            {MultiCurrency.formatAmountWithSymbol(
              Math.ceil(sliderValue * ratePerMessage),
              "INR"
            )}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <Button
          className="w-44 md:w-52 !h-9 md:!h-10"
          style={{
            color: colors.text_button,
            background: colors.whatsapp.primary,
          }}
        >
          Learn more
        </Button>
        <span
          className="mt-2 md:mt-4 font-light text-xs md:text-sm leading-normal"
          style={{ color: colors.text, opacity: 0.4 }}
        >
          *1 credit = 1 WhatsApp message
        </span>
      </div>
      <div className="w-full flex justify-center items-center">
        <div
          className="flex flex-col md:flex-row max-w-5xl flex-wrap md:items-center md:justify-center gap-2 md:gap-6"
          style={{ color: colors.text }}
        >
          <div className="flex items-center gap-2">
            <img
              src="/assets/icons/tick-green.svg"
              className="h-[0.875rem] w-[0.875rem] md:h-4 md:w-4"
            />
            <span className="text-sm md:text-xl leading-normal font-semibold">
              Curated messages for high conversion
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img
              src="/assets/icons/tick-green.svg"
              className="h-[0.875rem] w-[0.875rem] md:h-4 md:w-4"
            />
            <span className="text-sm md:text-xl leading-normal font-semibold">
              Engagement analytics
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img
              src="/assets/icons/tick-green.svg"
              className="h-[0.875rem] w-[0.875rem] md:h-4 md:w-4"
            />
            <span className="text-sm md:text-xl leading-normal font-semibold">
              Send message from verified account
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="py-9 px-5 md:px-0 md:py-[3.75rem] flex flex-col gap-6 md:gap-9">
      <h1 className="text-center mx-auto text-[1.5rem] md:text-[2.5rem] leading-normal">
        <span className="font-extrabold" style={{ color: colors.primary }}>
          1000+
        </span>
        <span className="font-bold" style={{ color: colors.text }}>
          {" "}
          creators love us
        </span>
      </h1>
      <Testimonials colors={colors.testimonialCard} />
    </section>
  );
}

export function FAQSection() {
  return (
    <section className="py-9 px-5 md:py-[3.75rem] flex flex-col items-center gap-6 md:gap-9">
      <h1
        className="text-center mx-auto text-2xl md:text-4xl font-bold leading-normal"
        style={{ color: colors.text }}
      >
        FAQs
      </h1>
      <div className="max-w-2xl flex flex-col gap-3 md:gap-6">
        <FAQ
          faqs={faqs}
          current_screen={currentScreen}
          faqAnswerBackground={colors.faq.answerBackground}
          faqQuestionBackground={colors.faq.questionBackground}
          isLightMode
          fullWidth
        />
      </div>
    </section>
  );
}
